import { encodeCameraParameters } from "@/components/r3f/utils/camera-parameters";
import { encodeOrientedBoundingBox } from "@/components/r3f/utils/oriented-bounding-box";
import { isFlyWalkImageElement } from "@/modes/flywalk-mode/flywalk-types";
import type { ModeNames } from "@/modes/mode";
import { setActiveCad } from "@/store/cad/cad-slice";
import { setClippingBox } from "@/store/clipping-box-slice";
import {
  changeMode,
  savedViewApplied,
  type ModeInitialState,
} from "@/store/mode-slice";
import { setFlyWalkSceneFilter } from "@/store/modes/flywalk-mode-slice";
import {
  setCompareShouldUseIntensityData,
  setShouldUseIntensityData,
} from "@/store/modes/walk-mode-slice";
import {
  initialState as PROJECT_FILTERING_INITIAL_STATE,
  setProjectFiltering,
} from "@/store/project-filtering/project-filtering-slice";
import { setActiveElement } from "@/store/selections-slice";
import type { AppThunk, RootState } from "@/store/store";
import { activateTool, deactivateTool, ToolName } from "@/store/ui/ui-slice";
import { SceneFilter } from "@/types/scene-filter";
import type { GUID } from "@faro-lotv/foundation";
import {
  isIElementImg360GrayScale,
  isIElementModel3dStream,
} from "@faro-lotv/ielement-types";
import { selectIElement } from "@faro-lotv/project-source";
import type { SavedViewData } from "@faro-lotv/sphere-viewer-contracts";

/**
 * Applies a validated saved view to the store in a single, self-contained step.
 *
 * This is the counterpart to `captureSavedView`, and replaces the scattered deep
 * link application (a burst of dispatches in the start mode plus a separate
 * effect for the clipping box): everything a saved view restores lives here,
 * which makes the payload easy to extend. To restore more state, capture a new
 * field and dispatch it from this thunk.
 *
 * The view is the whole truth: an absent field clears the corresponding state
 * rather than leaving whatever the session had. Capture omits a field precisely
 * when it was inactive (no cad, no filters, no tool), so merging instead of
 * replacing would open a shared view with the *viewer's* stale cad, filters or
 * tool still on. This matters because the thunk is reusable mid-session, not only
 * at startup where the store is empty anyway.
 *
 * Validating the view against the current project (does the element exist, can
 * the mode be started, is the tool permitted) is the caller's job.
 *
 * Applying also records *which* view is now applied, because that record is the
 * only thing the start mode's guard reads: a caller that restored a view without
 * it would let the start mode apply the same view again on a later return and
 * discard what the user changed since. Whether to *skip* an application is not
 * decided here - that rule belongs to the start mode, since a deliberate
 * re-apply (from a list of saved views, say) has to go through.
 *
 * @param view The validated saved view to apply.
 * @returns a thunk that restores the saved view's state.
 */
export function applySavedView(view: ValidSavedView): AppThunk<void> {
  return (dispatch, getState) => {
    const state = getState();

    dispatch(setActiveElement(view.id));
    dispatch(
      changeMode({ mode: view.mode, initialState: toModeInitialState(view) }),
    );
    dispatch(setActiveCad(view.cadId));

    const intensity = shouldUseIntensity(view.intensity, view.id, state);
    if (intensity !== undefined) {
      dispatch(setShouldUseIntensityData(intensity));
    }
    const rightIntensity = shouldUseIntensity(
      view.rightIntensity,
      view.rightId,
      state,
    );
    if (rightIntensity !== undefined) {
      dispatch(setCompareShouldUseIntensityData(rightIntensity));
    }

    const scene = sceneFilter(view, state);
    if (scene) dispatch(setFlyWalkSceneFilter(scene));

    dispatch(setClippingBox(view.clipping));
    dispatch(
      setProjectFiltering(view.filtering ?? PROJECT_FILTERING_INITIAL_STATE),
    );
    dispatch(view.tool ? activateTool(view.tool) : deactivateTool());

    dispatch(savedViewApplied(view));
  };
}

/**
 * A saved view that has been validated against the current project, mirroring
 * `ValidDeepLink`.
 *
 * The contract keeps `mode` and `tool` as plain strings, because the api cannot
 * know the viewer's enums. Applying the view needs the real enums to dispatch,
 * and a resolver has to check them against the project anyway (does the element
 * exist, can the mode be started, is the tool permitted), so narrowing them is
 * the resolver's output and this thunk's input.
 */
export type ValidSavedView = Omit<
  SavedViewData,
  "mode" | "tool" | "scene" | "rightScene"
> & {
  /** In a valid saved view the mode is always defined. */
  mode: ModeNames;
  /** Narrowed from the contract's plain string. */
  tool?: ToolName;
  /** Narrowed from the contract's plain string. */
  scene?: SceneFilter;
  /** Narrowed from the contract's plain string. */
  rightScene?: SceneFilter;
};

/**
 * Converts a structured saved view into the encoded mode initial state consumed
 * by the mode machinery (`changeMode` and the mode transition manager).
 *
 * This is the only place that re-encodes values: a saved view stores them
 * structured, while the existing mode pipeline still expects the compact string
 * encoding. Keeping the conversion here leaves that pipeline, and every mode,
 * untouched.
 *
 * It takes a plain {@link SavedViewData} rather than a {@link ValidSavedView}
 * because it reads none of the narrowed fields: the resolver needs the encoded
 * state to run a mode's `parse` *before* it can narrow `mode` and `tool`.
 *
 * @param view The saved view, validated or not.
 * @returns the encoded mode initial state.
 */
export function toModeInitialState(view: SavedViewData): ModeInitialState {
  const modeState: ModeInitialState = {};
  if (view.camera) modeState.camera = encodeCameraParameters(view.camera);
  if (view.clipping) {
    modeState.clipping = encodeOrientedBoundingBox(view.clipping);
  }
  if (view.scene) modeState.scene = view.scene;
  if (view.lookAtId) modeState.lookAtId = view.lookAtId;
  if (view.rightId) modeState.rightId = view.rightId;
  if (view.rightCam) modeState.rightCam = encodeCameraParameters(view.rightCam);
  if (view.rightScene) modeState.rightScene = view.rightScene;
  if (view.syncRot) modeState.syncRot = view.syncRot;
  if (view.syncWaypoint) modeState.syncWaypoint = view.syncWaypoint;
  return modeState;
}

/**
 * The scene filter the main view should be rendered with.
 *
 * A view captured in a mode that has a scene filter always stores one, so that
 * value wins. A view that carries none (a legacy deep link, or a context-free
 * view that only points at an element) leaves the choice to the element type,
 * which is what `FlyWalkTransition` does when it starts a mode with no initial
 * state at all. That fallback cannot run here, because applying a view always
 * hands the mode an initial state, so without this the filter the viewer
 * happened to be using would survive into the restored view.
 *
 * @param view The validated saved view.
 * @param state The current app state.
 * @returns the scene filter to select, or undefined to leave it alone.
 */
function sceneFilter(
  view: ValidSavedView,
  state: RootState,
): SceneFilter | undefined {
  if (view.scene) return view.scene;
  const element = view.id ? selectIElement(view.id)(state) : undefined;
  if (!element) return undefined;
  if (isFlyWalkImageElement(element)) return SceneFilter.Pano;
  if (isIElementModel3dStream(element)) return SceneFilter.Cad;
  return undefined;
}

/**
 * Whether the intensity panorama images should be preferred for an element.
 *
 * A saved view captures the flag explicitly, so that value always wins. Legacy
 * deep links carry no such field, and the app used to infer it from the element
 * type instead; that inference is kept as the fallback so a legacy link converted
 * into a saved view keeps opening the way it does today.
 *
 * @param explicit The value stored in the saved view, if any.
 * @param elementId The element the view renders, if any.
 * @param state The current app state.
 * @returns whether to prefer intensity images, or undefined to leave it alone.
 */
function shouldUseIntensity(
  explicit: boolean | undefined,
  elementId: GUID | undefined,
  state: RootState,
): boolean | undefined {
  if (explicit !== undefined) return explicit;
  const element = elementId ? selectIElement(elementId)(state) : undefined;
  return element && isIElementImg360GrayScale(element) ? true : undefined;
}
